
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useMemo } from 'react';
import flush from 'just-flush';
import useTranslation from 'next-translate/useTranslation';
import { TwitterShareButton, LinkedinShareButton } from 'react-share';
import { DateTime } from 'luxon';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { ArticleDetails, Divider, NewsGrid, PageIntro } from '@stereoagency/ui';
import { FaTwitter, FaLinkedin } from 'react-icons/fa';
import { useRouter } from 'next/router';
import { clientFactory, useFragment } from '@/gql';
import { acfFirst, findInArrayWaterfall, normalizeLocale, parseHtml, withPrepass } from '@/utils';
import { FindAllPostSummariesQueryDocument, FindPostDetailByIdQueryDocument, FindPostSummariesForSlugOrIdQueryDocument, FindRelatedPostSummariesQueryDocument, LanguageCodeFilterEnum, PostStatusEnum, } from '@/gql/client/graphql';
import { LanguageFieldsFragment, MediaItemFieldsFragment, PostFieldsFragment, PostSummaryFieldsFragment, TermNodeFieldsFragment, } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { ContentRenderer, Layout, SeoHead } from '@/components';
import { useMediaItems } from '@/hooks';
type UrlParams = {
    slugOrId: string;
};
type PageProps = {
    databaseId: number;
    preview?: boolean;
};
export default function Page({ databaseId, preview }: PageProps) {
    const router = useRouter() as ReturnType<typeof useRouter> | null;
    const { t } = useTranslation();
    const [{ data }] = useQuery({
        query: FindPostDetailByIdQueryDocument,
        variables: { id: String(databaseId), asPreview: Boolean(preview) }
    });
    const fields = useFragment(PostFieldsFragment, data?.post);
    const summaryFields = useFragment(PostSummaryFieldsFragment, fields);
    const featuredImageFields = useFragment(MediaItemFieldsFragment, summaryFields?.featuredImage?.node);
    const categories = useFragment(TermNodeFieldsFragment, flush(summaryFields?.categories?.nodes ?? []));
    const [{ data: relatedPostsQuery }] = useQuery({
        query: FindRelatedPostSummariesQueryDocument,
        pause: !summaryFields,
        variables: {
            postId: String(summaryFields?.databaseId),
            categories: categories.map((cat) => String(cat.databaseId)).sort(),
            first: 3
        }
    });
    const relatedPostsFields = useFragment(PostSummaryFieldsFragment, flush(relatedPostsQuery?.posts?.nodes ?? []));
    const mediaItems = useMediaItems(useMemo(() => flush(relatedPostsFields.map((post) => {
        const mediaItem = useFragment(MediaItemFieldsFragment, post.featuredImage?.node);
        return mediaItem?.databaseId ?? null;
    })), [relatedPostsFields]));
    const shareUrl = new URL(router?.asPath ?? '/', process.env.BASE_URL);
    const articleDetails = useMemo(() => {
        if (summaryFields) {
            return (<ArticleDetails info={[
                    t('pages--blog:by-author', { author: summaryFields.author?.node?.name }),
                    DateTime.fromISO(summaryFields.dateGmt ?? new Date().toISOString()).toLocaleString(DateTime.DATE_FULL),
                ]} tags={summaryFields.tags?.nodes?.map((tag) => ({
                    href: useFragment(TermNodeFieldsFragment, tag)?.uri ?? '/',
                    label: useFragment(TermNodeFieldsFragment, tag)?.name ?? ''
                }))} shareButtons={[
                    <TwitterShareButton url={shareUrl.toString()}>
              <FaTwitter />
            </TwitterShareButton>,
                    <LinkedinShareButton url={shareUrl.toString()}>
              <FaLinkedin />
            </LinkedinShareButton>,
                ]}/>);
        }
        return null;
    }, [router?.asPath, summaryFields]);
    if (!data || !fields || !summaryFields)
        return null;
    return (<Layout preview={preview} theme={acfFirst(fields.pageCustomFields?.theme) === 'dark' ? 'dark' : 'light'} enableLines={fields.pageCustomFields?.enableLines ?? false} useFixedNavbar={fields.pageCustomFields?.enableOverlayNavbar ?? false} hideFooterDivider={fields.pageCustomFields?.hideFooterDivider ?? false} hideSidetext={fields.pageCustomFields?.hideSidetext ?? false} sidetext={fields.pageCustomFields?.sidetext?.text
            ? {
                text: fields.pageCustomFields.sidetext.text,
                placement: (acfFirst(fields.pageCustomFields.sidetext.placement) ?? 'left') as 'left' | 'right'
            }
            : undefined}>
      <SeoHead seo={fields.seo ?? undefined}/>
      <div>
        <PageIntro variant="bigtitle" enableLines={fields.pageCustomFields?.enableLines ?? false} label={flush(summaryFields.categories?.nodes.map((cat) => {
            const catFields = useFragment(TermNodeFieldsFragment, cat);
            return catFields.name;
        }) ?? []).join(', ')} title={summaryFields.title ?? undefined} content={summaryFields.excerpt ? parseHtml(summaryFields.excerpt) : undefined} image={featuredImageFields?.mediaItemUrl
            ? {
                alt: featuredImageFields.altText ?? '',
                src: featuredImageFields.mediaItemUrl,
                width: featuredImageFields.mediaDetails?.width ?? 1920,
                height: featuredImageFields.mediaDetails?.height ?? 1080
            }
            : undefined} details={articleDetails}/>
        <Divider background="transparent" disablePadding variant="full-width" className="py-0"/>
      </div>
      <ContentRenderer enableLines={fields.pageCustomFields?.enableLines ?? false} html={fields.content ?? ''}/>
      <div className="container">{articleDetails}</div>
      {relatedPostsFields.length > 0 && (<>
          <Divider variant="full-width" background="transparent"/>
          <NewsGrid label={t('pages--blog:label')} title={t('pages--blog:related-news')} cards={flush(relatedPostsFields.map((post) => {
                const categoriesFields = useFragment(TermNodeFieldsFragment, flush(post.categories?.nodes ?? []));
                const mediaItemFields = useFragment(MediaItemFieldsFragment, post.featuredImage?.node);
                const mediaItem = mediaItemFields?.databaseId ? mediaItems.get(mediaItemFields.databaseId) : null;
                if (!mediaItem?.mediaItemUrl)
                    return null;
                return {
                    title: post.title ?? '',
                    labels: flush(categoriesFields.map((cat) => cat.name
                        ? {
                            label: cat.name,
                            href: cat.uri ?? '/'
                        }
                        : null)),
                    image: {
                        src: mediaItem.mediaItemUrl,
                        width: mediaItem.mediaDetails?.width ?? 320,
                        height: mediaItem.mediaDetails?.height ?? 200
                    },
                    callToAction: {
                        label: t('global:read-more'),
                        href: post.uri ?? '/'
                    }
                };
            }))}/>
        </>)}
    </Layout>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    type PathsType = GetStaticPathsResult<UrlParams>['paths'];
    const client = clientFactory();
    const allPosts = await client.query(FindAllPostSummariesQueryDocument, {
        lang: LanguageCodeFilterEnum.All
    }).toPromise();
    const nodes = allPosts.data?.posts?.nodes ?? [];
    return {
        paths: flush(nodes.reduce<PathsType>((acc, node) => {
            const fields = useFragment(PostSummaryFieldsFragment, node);
            const language = useFragment(LanguageFieldsFragment, fields?.language);
            if (!fields?.slug)
                return acc;
            acc.push({
                locale: language?.code ? normalizeLocale(language?.code) : i18n.defaultLocale,
                params: { slugOrId: fields.slug! }
            });
            return acc;
        }, [])),
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.slugOrId) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async ({ urqlClient }) => {
            const queryParams = Number.isNaN(Number(params.slugOrId)) ? { slug: params.slugOrId } : { databaseId: Number(params.slugOrId) };
            const { data: postsQuery, error } = await urqlClient.query(FindPostSummariesForSlugOrIdQueryDocument, {
                ...queryParams,
                stati: preview ? [PostStatusEnum.Draft, PostStatusEnum.Publish] : [PostStatusEnum.Publish]
            }).toPromise();
            if (error) {
                throw error;
            }
            const posts = useFragment(PostSummaryFieldsFragment, flush(postsQuery?.posts?.nodes ?? []));
            const post = findInArrayWaterfall(posts, [locale ?? i18n.defaultLocale, i18n.defaultLocale], (entry, param) => {
                const language = useFragment(LanguageFieldsFragment, entry.language);
                return Boolean(language?.code && locale === normalizeLocale(param));
            });
            if (!post) {
                return {
                    notFound: true
                };
            }
            return {
                preview: !!preview,
                databaseId: post.databaseId
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__1a1a7090b8c__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/blog/[slugOrId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1a7090b8c__ as getStaticProps }
  